/**
 * AdminProfil.jsx — Zentrale Profil-/Konto-Seite für Admins
 *
 * Bündelt: Mein Konto · Firmendaten (Link) · Standorte (Link) ·
 *          Module (per-Standort) · Rechnungen (Link) · Einstellungen · Freunde einladen (Link)
 *
 * Alte Routen /admin/company, /admin/settings, /admin/invoices bleiben
 * als Redirects erhalten (App.js).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useModules } from '../../contexts/ModulesContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  User, Building, MapPin, ToggleRight, Receipt, Settings, Gift,
  ChevronRight, Loader2, ExternalLink, Shield, Trash2, Check, X,
  Tag, CreditCard, Bell, Package, Info, Save, Hash, PenLine, Copy,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Sektions-Definitionen ─────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'konto',       label: 'Mein Konto',       icon: User },
  { id: 'firmendaten', label: 'Firmendaten',       icon: Building },
  { id: 'standorte',   label: 'Standorte',         icon: MapPin },
  { id: 'module',      label: 'Module',            icon: ToggleRight },
  { id: 'rechnungen',  label: 'Rechnungen',        icon: Receipt },
  { id: 'einstellungen', label: 'Einstellungen',   icon: Settings },
  { id: 'freunde',     label: 'Freunde einladen',  icon: Gift },
];

// ── Hilfsfunktion: Scroll zu Sektion ─────────────────────────────────────────
function scrollToSection(id) {
  const el = document.getElementById(`profil-${id}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ── Sektion-Wrapper ───────────────────────────────────────────────────────────
function ProfilSection({ id, title, icon: Icon, children }) {
  return (
    <div id={`profil-${id}`} className="scroll-mt-6">
      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ── Link-Karte ────────────────────────────────────────────────────────────────
function LinkCard({ title, description, to, badge, icon: Icon, color = 'text-primary' }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)}
      className="w-full flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors text-left group"
      data-testid={`profil-link-${to.replace(/\//g, '-')}`}>
      <div className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{title}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {badge != null && (
        <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-medium text-muted-foreground shrink-0">
          {badge}
        </span>
      )}
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0 transition-colors" />
    </button>
  );
}

// ── Modul-Toggle-Zeile ────────────────────────────────────────────────────────
function ModulRow({ module, onToggle, toggling }) {
  const isToggling = toggling === module.module_id;
  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0"
      data-testid={`modul-row-${module.module_id}`}>
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-medium">{module.name}</p>
        {module.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{module.description}</p>
        )}
      </div>
      {isToggling
        ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        : <Switch checked={module.is_enabled}
            onCheckedChange={(checked) => onToggle(module.module_id, module.is_enabled)}
            data-testid={`modul-toggle-${module.module_id}`} />
      }
    </div>
  );
}

// ── Haupt-Export ──────────────────────────────────────────────────────────────
// ── MinijobAngabenPanel: Betriebsnummer + Unterzeichner für RV-Befreiungsantrag ──
function MinijobAngabenPanel({ locations, headers }) {
  const [selLocId, setSelLocId] = useState(locations[0]?.id || '');
  const [betriebsnr, setBetriebsnr] = useState('');
  const [unterzeichner, setUnterzeichner] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selLocId) return;
    const loc = locations.find(l => l.id === selLocId);
    setBetriebsnr(loc?.legal_betriebsnummer || '');
    setUnterzeichner(loc?.legal_unterzeichner || '');
  }, [selLocId, locations]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/locations/${selLocId}/legal-profile`,
        { legal_betriebsnummer: betriebsnr.trim(), legal_unterzeichner: unterzeichner.trim() },
        { headers });
      toast.success('Minijob-Angaben gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 border border-amber-200 dark:border-amber-800 rounded-xl p-4 bg-amber-50/40 dark:bg-amber-900/10 space-y-3"
      data-testid="minijob-angaben-panel">
      <div className="flex items-center gap-2">
        <Hash className="h-4 w-4 text-amber-600 shrink-0" />
        <p className="text-sm font-semibold">Angaben für RV-Befreiungsantrag (Minijob)</p>
      </div>
      <p className="text-xs text-muted-foreground">
        Werden automatisch in jeden neuen Befreiungsantrag eingetragen. Einmalig pro Standort hinterlegen.
      </p>
      {locations.length > 1 && (
        <select value={selLocId} onChange={e => setSelLocId(e.target.value)}
          className="h-8 rounded-lg border border-border bg-background px-2 text-xs w-full">
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">
            Betriebsnummer (8-stellig, Minijob-Zentrale)
          </label>
          <Input value={betriebsnr} onChange={e => setBetriebsnr(e.target.value)}
            placeholder="z.B. 12345678" maxLength={8}
            className="h-8 text-sm font-mono" data-testid="betriebsnummer-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">
            Unterzeichner (Name AG-Vertreter)
          </label>
          <Input value={unterzeichner} onChange={e => setUnterzeichner(e.target.value)}
            placeholder="z.B. Ali Tombul"
            className="h-8 text-sm" data-testid="unterzeichner-input" />
        </div>
      </div>
      <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5"
        data-testid="minijob-angaben-save-btn">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        Speichern
      </Button>
    </div>
  );
}

export default function AdminProfil() {
  const { user } = useAuth();
  const { refreshModules } = useModules();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [activeSection, setActiveSection] = useState('konto');
  const observerRef = useRef(null);

  // ── Konto-Daten ──────────────────────────────────────────────────────────
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [kdnrCopied, setKdnrCopied] = useState(false);

  const handleCopyKdnr = async () => {
    const val = subscriptionData?.customer_number;
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      setKdnrCopied(true);
      toast.success('Kundennummer kopiert');
      setTimeout(() => setKdnrCopied(false), 2000);
    } catch {
      toast.error('Kopieren nicht möglich');
    }
  };

  // ── Standorte ──────────────────────────────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');

  // ── Module ──────────────────────────────────────────────────────────────
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [togglingModule, setTogglingModule] = useState(null);

  // ── URL-Hash → Scroll ────────────────────────────────────────────────────
  useEffect(() => {
    const hash = routeLocation.hash?.replace('#', '');
    if (hash && SECTIONS.find(s => s.id === hash)) {
      setTimeout(() => scrollToSection(hash), 150);
      setActiveSection(hash);
    }
  }, [routeLocation.hash]);

  // ── IntersectionObserver für aktive Sektion ──────────────────────────────
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace('profil-', '');
            setActiveSection(id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(`profil-${s.id}`);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  // ── Daten laden ──────────────────────────────────────────────────────────
  useEffect(() => {
    // Standorte
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => {
        const locs = r.data || [];
        setLocations(locs);
        if (locs.length > 0) setSelectedLocId(locs[0].id);
      }).catch(() => {});

    // Abo-Status: admin/subscription liefert korrekte V2-Paketinfos
    axios.get(`${API}/api/admin/subscription`, { headers: authH() })
      .then(r => setSubscriptionData(r.data))
      .catch(() => {});
  }, []);

  // ── Module für ausgewählten Standort ─────────────────────────────────────
  const fetchModules = useCallback(async (locId) => {
    if (!locId) return;
    setModulesLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/locations/${locId}/modules`, { headers: authH() });
      setModules(r.data.modules || []);
    } catch { toast.error('Module konnten nicht geladen werden'); }
    finally { setModulesLoading(false); }
  }, []);

  useEffect(() => { if (selectedLocId) fetchModules(selectedLocId); }, [selectedLocId, fetchModules]);

  const handleToggleModule = async (moduleId, currentlyEnabled) => {
    setTogglingModule(moduleId);
    try {
      await axios.post(`${API}/api/admin/locations/${selectedLocId}/toggle-module`,
        { module_id: moduleId, enabled: !currentlyEnabled },
        { headers: authH() }
      );
      toast.success(currentlyEnabled ? 'Modul ausgeblendet' : 'Modul eingeblendet');
      await fetchModules(selectedLocId);
      refreshModules(); // Sidebar neu berechnen
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Umschalten');
    } finally { setTogglingModule(null); }
  };

  // ── Seitenleiste ─────────────────────────────────────────────────────────
  const Sidebar = () => (
    <nav className="hidden lg:block sticky top-6 w-52 shrink-0">
      <div className="rounded-xl border border-border bg-card p-2 space-y-0.5">
        {SECTIONS.map(s => (
          <button key={s.id}
            onClick={() => { scrollToSection(s.id); setActiveSection(s.id); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              activeSection === s.id
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            data-testid={`profil-nav-${s.id}`}>
            <s.icon className="h-4 w-4 shrink-0" />
            {s.label}
          </button>
        ))}
      </div>
    </nav>
  );

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Mobile-Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-3 mb-6 lg:hidden border-b border-border">
          {SECTIONS.map(s => (
            <button key={s.id}
              onClick={() => { scrollToSection(s.id); setActiveSection(s.id); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap font-medium transition-colors shrink-0 ${
                activeSection === s.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
              <s.icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex gap-8">
          <Sidebar />

          {/* Inhalte */}
          <div className="flex-1 min-w-0 space-y-10">

            {/* ── Mein Konto ─────────────────────────────────────────── */}
            <ProfilSection id="konto" title="Mein Konto" icon={User}>
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-base">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <span className="inline-flex items-center gap-1 mt-1 text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                      <Shield className="h-3 w-3" />
                      {user?.role === 'admin' ? 'Administrator' : user?.role}
                    </span>
                  </div>
                </div>
                {subscriptionData?.package && (
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Package className="h-3.5 w-3.5" />
                        Paket
                      </span>
                      <span className="font-medium">
                        {subscriptionData.package.name || subscriptionData.package.id || '–'}
                        {subscriptionData.subscription?.status === 'trialing' && (
                          <span className="ml-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                            Testphase
                          </span>
                        )}
                      </span>
                    </div>
                    {subscriptionData.subscription?.status === 'trialing' &&
                     subscriptionData.subscription?.current_period_end && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Testphase aktiv
                      </p>
                    )}
                  </div>
                )}
                {subscriptionData?.customer_number && (
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Hash className="h-3.5 w-3.5" />
                        Kundennummer
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono font-medium" data-testid="konto-kundennummer">
                          {subscriptionData.customer_number}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyKdnr}
                          title="Kundennummer kopieren"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          data-testid="konto-kundennummer-copy">
                          {kdnrCopied
                            ? <Check className="h-3.5 w-3.5 text-green-600" />
                            : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </ProfilSection>
            <ProfilSection id="firmendaten" title="Firmendaten" icon={Building}>
              <p className="text-sm text-muted-foreground mb-3">
                Firmenname, Adresse, Rechnungsangaben und weitere Unternehmenseinstellungen.
              </p>
              <LinkCard
                title="Firmendaten bearbeiten"
                description="Firmenname, Adresse, Mehrwertsteuer, Brevo-Einstellungen"
                to="/admin/company"
                icon={Building}
                color="text-blue-600"
              />
            </ProfilSection>

            {/* ── Standorte ──────────────────────────────────────────── */}
            <ProfilSection id="standorte" title="Standorte" icon={MapPin}>
              <p className="text-sm text-muted-foreground mb-3">
                Standorte verwalten, neue anlegen und modul-spezifische Einstellungen pro Standort konfigurieren.
              </p>
              <LinkCard
                title="Standorte verwalten"
                description="Standorte erstellen, bearbeiten und Module pro Standort konfigurieren"
                to="/admin/locations"
                icon={MapPin}
                badge={locations.length > 0 ? `${locations.length} Standort${locations.length !== 1 ? 'e' : ''}` : undefined}
                color="text-green-600"
              />
              {/* Fix 2: Minijob-Angaben für RV-Befreiungsantrag */}
              {locations.length > 0 && (
                <MinijobAngabenPanel locations={locations} headers={authH()} />
              )}
            </ProfilSection>

            {/* ── Module ─────────────────────────────────────────────── */}
            <ProfilSection id="module" title="Module" icon={ToggleRight}>
              <p className="text-sm text-muted-foreground mb-4">
                Wähle welche Module in der Navigation sichtbar sein sollen — pro Standort unabhängig einstellbar.
                {locations.length > 1 && ' Ein Modul wird aus der Sidebar ausgeblendet sobald es an allen Standorten deaktiviert ist.'}
              </p>

              {/* Standort-Selector bei mehreren Standorten */}
              {locations.length > 1 && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                    Standort auswählen
                  </label>
                  <select
                    value={selectedLocId}
                    onChange={e => setSelectedLocId(e.target.value)}
                    className="h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 w-full sm:w-auto"
                    data-testid="modul-location-select">
                    {locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {modulesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : modules.length === 0 ? (
                <div className="rounded-xl border border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                  <Info className="h-5 w-5 mx-auto mb-2 opacity-50" />
                  Keine Module verfügbar
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-card divide-y divide-border/50 overflow-hidden">
                  {/* Header */}
                  <div className="px-4 py-2.5 bg-muted/30 flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Modul</span>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sichtbar</span>
                  </div>
                  {/* Zeilen */}
                  <div className="px-4">
                    {modules.map(m => (
                      <ModulRow key={m.module_id} module={m}
                        onToggle={handleToggleModule} toggling={togglingModule} />
                    ))}
                  </div>
                </div>
              )}

              {modules.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Deaktivierte Module sind weiterhin im System vorhanden — sie werden nur aus der Navigation ausgeblendet.
                </p>
              )}
            </ProfilSection>

            {/* ── Rechnungen ─────────────────────────────────────────── */}
            <ProfilSection id="rechnungen" title="Rechnungen & Zahlungen" icon={Receipt}>
              <p className="text-sm text-muted-foreground mb-3">
                Rechnungshistorie, Zahlungsstatus und Abonnement-Übersicht.
              </p>
              <div className="space-y-2">
                <LinkCard
                  title="Rechnungen & Zahlungen"
                  description="Rechnungshistorie, Abonnement-Status und Zahlungsdetails"
                  to="/admin/invoices"
                  icon={Receipt}
                  color="text-yellow-600"
                />
                <LinkCard
                  title="Zahlungsmethode ändern"
                  description="Kreditkarte, SEPA oder andere Zahlungsmethoden verwalten"
                  to="/admin/invoices"
                  icon={CreditCard}
                  color="text-emerald-600"
                />
              </div>
            </ProfilSection>

            {/* ── Einstellungen ──────────────────────────────────────── */}
            <ProfilSection id="einstellungen" title="Einstellungen" icon={Settings}>
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-3">Benutzerrollen</h3>
                  <div className="space-y-2">
                    {[
                      { role: 'Administrator', color: 'border-blue-500 bg-blue-50 dark:bg-blue-950/30', textColor: 'text-blue-900 dark:text-blue-100', desc: 'Vollzugriff auf alle Funktionen, Einstellungen und Standortverwaltung' },
                      { role: 'Stationsleitung', color: 'border-green-500 bg-green-50 dark:bg-green-950/30', textColor: 'text-green-900 dark:text-green-100', desc: 'Verwaltung des zugewiesenen Standorts, Aufgaben- und Mitarbeiterverwaltung' },
                      { role: 'Mitarbeiter', color: 'border-gray-400 bg-gray-50 dark:bg-gray-800/30', textColor: 'text-gray-900 dark:text-gray-100', desc: 'Bearbeitung von Aufgaben, Temperaturmessungen und Unterweisungen' },
                    ].map(r => (
                      <div key={r.role} className={`border-l-4 ${r.color} pl-3 py-2 rounded-r`}>
                        <p className={`text-sm font-medium ${r.textColor}`}>{r.role}</p>
                        <p className={`text-xs mt-0.5 opacity-80 ${r.textColor}`}>{r.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/admin/delete-account')}
                  className="flex items-center gap-2 text-sm text-red-500 hover:text-red-700 transition-colors px-1 py-1"
                  data-testid="profil-delete-account">
                  <Trash2 className="h-4 w-4" />
                  Account löschen
                </button>
              </div>
            </ProfilSection>

            {/* ── Freunde einladen ───────────────────────────────────── */}
            <ProfilSection id="freunde" title="Freunde einladen" icon={Gift}>
              <p className="text-sm text-muted-foreground mb-3">
                Lade andere Gasthändler zu AllFlowX ein und profitiere gemeinsam.
              </p>
              <LinkCard
                title="Referral-Programm"
                description="Einladungslink generieren und Prämien einsehen"
                to="/admin/referrals"
                icon={Gift}
                color="text-pink-600"
              />
            </ProfilSection>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
